import type { OrgRole, Permission, Tenant, User } from './types';
import { hasPermission } from './roles';

/**
 * 能力闸门 —— 前后端共用的唯一事实来源。
 *
 * 权限位回答「这个角色能不能做某类操作」，能力闸门再叠加两类前提：
 *   1. 邮箱是否已验证（自助注册的账号在验证前受限）；
 *   2. 企业是否已通过资质审核（待审核企业不能邀请成员、发 API Key、开票）。
 *
 * 前端用它们决定按钮是否可用，Worker 用同一份函数拒绝请求 ——
 * 不要在路由里手写 `kind === 'enterprise' && status === 'pending'`。
 */

/** 每个账号最多创建的组织数，防止批量注册囤积组织。 */
export const MAX_ORGS_PER_USER = 3;

export function isEmailVerified(user: Pick<User, 'emailVerified'> | null | undefined): boolean {
  return user?.emailVerified === true;
}

/** 个人组织天然通过；企业组织必须审核通过。 */
export function isTenantApproved(
  tenant: Pick<Tenant, 'kind' | 'verificationStatus'> | null | undefined,
): boolean {
  if (!tenant) return false;
  return tenant.kind === 'personal' || tenant.verificationStatus === 'verified';
}

export function canPurchase(
  tenant: Pick<Tenant, 'status' | 'kind' | 'verificationStatus'> | null | undefined,
  user: Pick<User, 'emailVerified'> | null | undefined,
): boolean {
  if (!tenant || tenant.status !== 'active') return false;
  return isEmailVerified(user);
}

/**
 * 按产品受众判断能否下单。
 *
 * 对公产品（`audience = 'enterprise'`）只向**已通过资质审核的企业组织**开放：
 * 个人组织、未认证或审核中的企业都不能购买，否则「把组织类型改成企业」就成了
 * 绕开资质审核购买对公服务的捷径。
 */
export function canPurchaseAudience(
  tenant: Pick<Tenant, 'status' | 'kind' | 'verificationStatus'> | null | undefined,
  user: Pick<User, 'emailVerified'> | null | undefined,
  audience: 'all' | 'personal' | 'enterprise' | undefined,
): boolean {
  if (!canPurchase(tenant, user)) return false;
  if (audience !== 'enterprise') return true;
  return tenant?.kind === 'enterprise' && tenant.verificationStatus === 'verified';
}

export function canInviteMembers(
  tenant: Pick<Tenant, 'status' | 'kind' | 'verificationStatus'> | null | undefined,
  user: Pick<User, 'emailVerified'> | null | undefined,
  role: OrgRole | null | undefined,
): boolean {
  return (
    isEmailVerified(user) &&
    isTenantApproved(tenant) &&
    hasPermission(role, 'member.invite')
  );
}

export function canManageApiKeys(
  tenant: Pick<Tenant, 'status' | 'kind' | 'verificationStatus'> | null | undefined,
  user: Pick<User, 'emailVerified'> | null | undefined,
  role: OrgRole | null | undefined,
): boolean {
  return (
    isEmailVerified(user) &&
    isTenantApproved(tenant) &&
    hasPermission(role, 'billing.manage')
  );
}

export function canRequestInvoice(
  tenant: Pick<Tenant, 'status' | 'kind' | 'verificationStatus'> | null | undefined,
  user: Pick<User, 'emailVerified'> | null | undefined,
  role: OrgRole | null | undefined,
): boolean {
  return (
    isEmailVerified(user) &&
    isTenantApproved(tenant) &&
    hasPermission(role, 'billing.invoice')
  );
}

/** 转让所有权、解散组织：必须企业已审核或为个人组织，且具备权限位。 */
export function canManageOwnership(
  tenant: Pick<Tenant, 'status' | 'kind' | 'verificationStatus'> | null | undefined,
  role: OrgRole | null | undefined,
  permission: Extract<Permission, 'org.delete' | 'member.manage'>,
): boolean {
  return isTenantApproved(tenant) && hasPermission(role, permission);
}
